import { Show, createSignal, For, createEffect } from 'solid-js';
import { Command } from '../../commands/types';
import Modal from '../common/Modal';

interface CommandPaletteProps {
	isOpen: boolean;
	onClose: () => void;
	commands: Command[];
}

export default function CommandPalette(props: CommandPaletteProps) {
	const [searchQuery, setSearchQuery] = createSignal('');
	const [selectedIndex, setSelectedIndex] = createSignal(0);

	let searchInputRef: HTMLInputElement | undefined;

	const filteredCommands = () => {
		const query = searchQuery().toLowerCase().trim();
		if (!query) return props.commands;

		return props.commands.filter(
			(command) => command.title.toLowerCase().includes(query) || command.description?.toLowerCase().includes(query),
		);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		const commands = filteredCommands();

		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault();
				setSelectedIndex((prev) => (prev + 1) % commands.length);
				break;
			}
			case 'ArrowUp': {
				event.preventDefault();
				setSelectedIndex((prev) => (prev - 1 + commands.length) % commands.length);
				break;
			}
			case 'Enter': {
				event.preventDefault();
				const selectedCommand = commands[selectedIndex()];
				if (selectedCommand) {
					selectedCommand.action();
					props.onClose();
				}
				break;
			}
		}
	};

	const executeCommand = (command: Command) => {
		command.action();
		props.onClose();
	};

	const handleSearchInput = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		setSearchQuery(target.value);
		setSelectedIndex(0); // Reset selection when search changes
	};

	// Reset state when modal closes
	const handleClose = () => {
		setSearchQuery('');
		setSelectedIndex(0);
		props.onClose();
	};

	// Focus search input when modal opens
	createEffect(() => {
		if (props.isOpen && searchInputRef) {
			// Small delay to ensure modal is rendered
			setTimeout(() => searchInputRef?.focus(), 50);
		}
	});

	return (
		<Modal isOpen={props.isOpen} onClose={handleClose}>
			<div class="w-full max-w-2xl" data-testid="command-palette">
				<div class="mb-4">
					<input
						ref={searchInputRef}
						type="text"
						placeholder="Type a command..."
						value={searchQuery()}
						onInput={handleSearchInput}
						onKeyDown={handleKeyDown}
						class="w-full border-2 border-green bg-black p-3 text-green outline-none placeholder:text-green placeholder:opacity-50"
					/>
				</div>

				<div class="max-h-96 overflow-y-auto">
					<Show when={filteredCommands().length === 0}>
						<div class="p-4 text-center text-green opacity-50">No commands found</div>
					</Show>

					<For each={filteredCommands()}>
						{(command, index) => (
							<div
								class="cursor-pointer border-b border-green border-opacity-20 p-3"
								classList={{
									'bg-green bg-opacity-20': index() === selectedIndex(),
								}}
								onClick={() => executeCommand(command)}
							>
								<div class="font-bold text-green">{command.title}</div>
								<Show when={command.description}>
									<div class="text-sm text-green opacity-70">{command.description}</div>
								</Show>
							</div>
						)}
					</For>
				</div>
			</div>
		</Modal>
	);
}
